// A PÁGINA DE ASSINAR (Wesley, 02/11/2026, noite): o app é só para assinantes.
// "O paywall já antes do cadastro: na 1ª vez que abre o app, uma página simples
// de assinar, com o que a pessoa vai ter e poder fazer, e o Login fácil no topo
// para quem já é assinante." Sem teste grátis (TESTE_DIAS=0): R$ 29,90 por ano,
// com 7 dias de garantia (o direito de arrependimento: reembolso).
//   - No app (lojas): "Assinar" → o cadastro simples (sem código por e-mail) →
//     a compra abre sozinha na loja (BloqueioPro com autoIniciar).
//   - No site: não se vende — "Assine pelo app" com as lojas.
// Aparece para quem não tem sessão e nunca entrou neste aparelho; quem já
// entrou cai direto no Login (ver App.tsx, `jaEntrouNoAparelho`).
import React, { useEffect, useState } from 'react';
import { api, type TrilhaDaOferta } from '../../services/apiV2';
import { isNativeApp } from '../platform';
import { LojasDoApp } from '../baixar-app';
import { Icon } from '../icons';
import { PRO_NOME, usePrecosPro } from './pro';
import './nova.css';

// A HABILIDADE ESCOLHIDA (03/11/2026): a que a pessoa veio ver pela DM do Reel
// (/api/oferta `sugerida`, pelo IP da página de habilidade) ou a que ela tocou
// aqui. A entrada da conta nova (entrada-curta.tsx) já abre com ela marcada.
const CHAVE_HABILIDADE = 'forja_habilidade';
export const habilidadeEscolhida = (): string | null => {
  try { return localStorage.getItem(CHAVE_HABILIDADE); } catch { return null; }
};
const guardarHabilidade = (slug: string) => {
  try { localStorage.setItem(CHAVE_HABILIDADE, slug); } catch { /* sem storage: a entrada usa o padrão */ }
};

type Formacao = { habilidades: number; areas: number; desafios: number; insights: number; etapas: number };

const O_QUE_VOCE_FAZ: { t: string; d: string }[] = [
  { t: 'Escolhe a habilidade', d: 'Cada uma tem a sua trilha, com o livro que a ensina. Você lê o seu exemplar de papel.' },
  { t: 'Lê no papel e registra a página', d: 'A cada trecho, o Mentor libera a nota daquela página: o contexto, onde as pessoas se confundem, um exemplo brasileiro.' },
  { t: 'Faz um desafio pequeno', d: 'Para aplicar no dia seguinte. É aí que a leitura vira prática.' },
  { t: 'Guarda tudo no Caderno', d: 'As suas respostas, livro por livro, para ver o que mudou na sua vida.' },
  { t: 'Mantém o ritmo', d: 'Meta diária, sequência de dias e o lembrete no horário que você escolher.' },
];

export const PaginaAssinar: React.FC<{
  garantiaDias: number;
  /** "Assinar" no app: abre o cadastro (e depois a compra). */
  onAssinar: () => void;
  /** "Login" no topo e no fim: quem já é assinante. */
  onLogin: () => void;
}> = ({ garantiaDias, onAssinar, onLogin }) => {
  const precos = usePrecosPro();
  const nativo = isNativeApp();
  const [trilhas, setTrilhas] = useState<TrilhaDaOferta[]>([]);
  const [formacao, setFormacao] = useState<Formacao | null>(null);
  const [escolhida, setEscolhida] = useState<string | null>(habilidadeEscolhida);
  useEffect(() => {
    let vivo = true;
    api.oferta().then((o) => {
      if (!vivo || !Array.isArray(o?.trilhas)) return;
      setTrilhas(o.trilhas);
      if (o.formacao) setFormacao(o.formacao);
      // A da DM vale mais que uma escolha antiga deste aparelho.
      if (o.sugerida && o.trilhas.some((t) => t.slug === o.sugerida)) { guardarHabilidade(o.sugerida); setEscolhida(o.sugerida); }
    }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  const escolher = (slug: string) => { guardarHabilidade(slug); setEscolhida(slug); };
  // A escolhida vem primeiro.
  const ordem = [...trilhas].sort((a, b) => Number(b.slug === escolhida) - Number(a.slug === escolhida));

  const botao = (onde: string) => nativo ? (
    <button className="nv-primario" onClick={onAssinar} data-assinar={onde}>
      Assinar por {precos.ano} por ano <Icon name="arrow-right" size={16} />
    </button>
  ) : null;

  return (
    <div className="nv nv-assinar" data-tela="pagina-assinar">
      <header className="nv-assinar-topo">
        <span className="nv-assinar-marca"><img src="/icons/icon-192.png" alt="" width="24" height="24" />Forjamente</span>
        <button className="nv-assinar-login" onClick={onLogin} data-login-topo>Login</button>
      </header>

      <section className="nv-assinar-hero nv-entra">
        <span className="nv-mono">{PRO_NOME}</span>
        <h1 className="nv-serif">Leia com um <em>mentor</em> do lado.</h1>
        <p className="nv-sub">Cada livro vira uma habilidade: insights na página certa, desafios para aplicar e um caderno para nunca esquecer.</p>
        {formacao && (
          <p className="nv-assinar-numeros" data-assinar-numeros>
            <b>{formacao.habilidades}</b> habilidades · <b>{formacao.desafios}</b> desafios práticos · <b>{formacao.insights}</b> insights
          </p>
        )}
      </section>

      {ordem.length > 0 && (
        <section className="nv-assinar-trilhas nv-entra nv-entra-2" data-assinar-habilidades={ordem.length}>
          <span className="nv-mono">Por onde você quer começar?</span>
          <div className="nv-assinar-habs" role="radiogroup" aria-label="A habilidade para começar">
            {ordem.map((l) => (
              <button key={l.slug} type="button" role="radio" aria-checked={l.slug === escolhida}
                className={`nv-assinar-hab${l.slug === escolhida ? ' nv-escolhida' : ''}`}
                onClick={() => escolher(l.slug)} data-habilidade={l.palavra || l.slug}>
                {l.capa ? <img src={l.capa} alt="" loading="lazy" /> : <span className="nv-assinar-sem-capa nv-serif">{l.titulo}</span>}
                <span className="nv-assinar-hab-txt">
                  {l.area && <span className="nv-assinar-hab-area">{l.area}</span>}
                  <b className="nv-serif">{l.habilidade || l.titulo}</b>
                  <span className="nv-assinar-hab-livro">{l.titulo}</span>
                  {(l.desafios || l.etapas) ? <span className="nv-assinar-hab-num">{[l.desafios ? `${l.desafios} desafios` : '', l.etapas ? `${l.etapas} etapas` : ''].filter(Boolean).join(' · ')}</span> : null}
                </span>
              </button>
            ))}
          </div>
          <p className="nv-assinar-livro" data-assinar-livro>Você lê no seu livro de papel; o Forjamente fica do lado.</p>
        </section>
      )}

      <section className="nv-assinar-oque nv-entra nv-entra-2">
        <span className="nv-mono">O que você vai fazer</span>
        <ol>
          {O_QUE_VOCE_FAZ.map((x, i) => (
            <li key={i}><b>{x.t}</b><span>{x.d}</span></li>
          ))}
        </ol>
      </section>

      <section className="nv-assinar-preco nv-entra nv-entra-3" data-assinar-preco>
        <div className="nv-pro-valor"><b className="nv-serif">{precos.ano}</b><span>por ano</span></div>
        <p>{precos.porMes ? `Menos de ${precos.porMes} por mês. ` : ''}{formacao ? `Todas as ${formacao.habilidades} habilidades` : 'Todas as trilhas'}, as notas do Mentor, os desafios e o Caderno.</p>
        {garantiaDias > 0 && (
          <div className="nv-assinar-garantia" data-garantia={garantiaDias}>
            <Icon name="check" size={16} />
            <span><b>{garantiaDias} dias de garantia.</b> Não gostou? Peça o reembolso em até {garantiaDias} dias da compra.</span>
          </div>
        )}
      </section>

      <section className="nv-assinar-acao nv-entra nv-entra-4" data-assinar-acao={nativo ? 'nativo' : 'site'}>
        {nativo ? (
          <>
            {botao('principal')}
            <p className="nv-assinar-letras">Primeiro você cria a conta (nome, e-mail e senha). Em seguida, confirma a assinatura na {/iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'App Store' : 'loja'}. Renova uma vez por ano; cancele quando quiser nas assinaturas da loja.</p>
          </>
        ) : (
          <div className="nv-bloqueio-loja" data-assine-pelo-app>
            <b className="nv-serif nv-bloqueio-titulo">Assine pelo app</b>
            <p>Baixe o Forjamente, crie a conta e assine pela App Store ou pelo Google Play.</p>
            <LojasDoApp de="pagina-assinar" escuro />
          </div>
        )}
        <p className="nv-assinar-letras">
          <a href="/termos" target="_blank" rel="noopener noreferrer">Termos de Uso</a>
          {' · '}
          <a href="/privacidade" target="_blank" rel="noopener noreferrer">Política de Privacidade</a>
          {' · '}
          <a href="/suporte#reembolso" target="_blank" rel="noopener noreferrer">Como pedir o reembolso</a>
        </p>
      </section>

      <div className="nv-assinar-ja">
        Já é assinante? <button onClick={onLogin} data-login-fim>Fazer login</button>
      </div>
    </div>
  );
};
